import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, Activity, Calendar, CreditCard, Menu, Bell, Users, ScrollText,
  MessageSquare, Shield, LogOut, User as UserIcon, LayoutDashboard,
  Trophy, Vote, ShieldAlert, Info, CheckCheck, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { notifApi } from "@/lib/api";
import { Logo, UserAvatar, ROLE_LABEL } from "@/components/shared";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageToggle } from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

const NAV = [
  { to: "/", label: "Accueil", labelKey: "nav.home", icon: Home, testid: "nav-accueil", end: true },
  { to: "/sports", label: "Sports", labelKey: "nav.sports", icon: Activity, testid: "nav-sports" },
  { to: "/calendrier", label: "Calendrier", labelKey: "nav.calendar", icon: Calendar, testid: "nav-calendrier" },
  { to: "/cotisations", label: "Cotisations", labelKey: "nav.dues", icon: CreditCard, testid: "nav-cotisations" },
  { to: "/social", label: "Réseau", labelKey: "nav.social", icon: MessageSquare, testid: "nav-social" },
  { to: "/repertoire", label: "Répertoire", labelKey: "nav.directory", icon: Users, testid: "nav-repertoire" },
  { to: "/reglements", label: "Règlements", labelKey: "nav.rules", icon: ScrollText, testid: "nav-reglements" },
];
const MOBILE_MAIN = [NAV[0], NAV[1], NAV[2], NAV[4]]; // Accueil, Sports, Calendrier, Réseau
const MOBILE_MORE = [NAV[3], NAV[5], NAV[6], { to: "/profil", label: "Mon profil", labelKey: "nav.profile", icon: UserIcon, testid: "nav-profil" }];

const CAT_ICON = { trophy: Trophy, event: Calendar, collecte: CreditCard, vote: Vote, discipline: ShieldAlert, general: Info };
const CAT_KEY = { trophy: "notif.cat.trophy", event: "notif.cat.event", collecte: "notif.cat.collecte", vote: "notif.cat.vote", discipline: "notif.cat.discipline", general: "notif.cat.general" };
const timeAgo = (iso) => { try { return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: fr }); } catch { return ""; } };

const NotificationPanel = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  const { t } = useI18n();

  const load = async () => {
    try {
      const [{ data: list }, { data: c }] = await Promise.all([notifApi.list(), notifApi.unreadCount()]);
      setItems(list); setCount(c.count);
    } catch { /* not logged in */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const openItem = async (n) => { try { await notifApi.read(n.id); } catch {} setOpen(false); if (n.link) navigate(n.link); load(); };
  const markAll = async () => { try { await notifApi.readAll(); } catch {} load(); };

  const grouped = items.reduce((acc, n) => { (acc[n.category] = acc[n.category] || []).push(n); return acc; }, {});

  return (
    <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <SheetTrigger asChild>
        <button data-testid="notifications-bell-bouton" aria-label="Notifications"
          className="relative rounded-full p-2 hover:bg-muted transition-colors">
          <Bell className="h-5 w-5 text-foreground" />
          <AnimatePresence>
            {count > 0 && (
              <motion.span data-testid="notifications-count" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-secondary px-1 text-[10px] font-bold text-white">
                {count > 9 ? "9+" : count}
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full max-w-sm p-0" data-testid="notifications-panneau">
        <SheetHeader className="flex-row items-center justify-between border-b p-4">
          <SheetTitle className="flex items-center gap-2"><Bell className="h-4 w-4 text-secondary" /> {t("nav.notifications")}</SheetTitle>
          <button onClick={markAll} className="text-xs text-secondary hover:underline inline-flex items-center gap-1" data-testid="notif-panneau-tout-lu"><CheckCheck className="h-3.5 w-3.5" /> {t("notif.allRead")}</button>
        </SheetHeader>
        <div className="max-h-[calc(100vh-70px)] overflow-y-auto p-3 space-y-4">
          {items.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">{t("notif.empty")}</div>
          ) : Object.entries(grouped).map(([cat, list]) => {
            const Icon = CAT_ICON[cat] || Info;
            return (
              <div key={cat}>
                <div className="mb-1.5 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {t(CAT_KEY[cat] || "notif.cat.general", cat)}</div>
                <div className="space-y-1.5">
                  {list.map((n) => (
                    <button key={n.id} onClick={() => openItem(n)} data-testid="notif-panneau-item"
                      className={cn("flex w-full flex-col items-start gap-0.5 rounded-xl border p-3 text-left transition-colors hover:bg-muted", !n.read && "border-secondary/40 bg-secondary/5")}>
                      <div className="flex w-full items-center justify-between gap-2"><span className="text-sm font-medium leading-tight">{n.title}</span>{!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-secondary" />}</div>
                      <span className="text-xs text-muted-foreground line-clamp-2">{n.body}</span>
                      <span className="text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const AvatarMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  if (!user) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="avatar-menu-bouton" className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <UserAvatar user={user} size="h-9 w-9" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="font-semibold">{user.first_name} {user.last_name}</div>
          <div className="text-xs font-normal text-muted-foreground">{ROLE_LABEL[user.role]}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/profil")} data-testid="menu-profil"><UserIcon className="mr-2 h-4 w-4" /> {t("nav.profile")}</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/tableau-de-bord")} data-testid="menu-dashboard"><LayoutDashboard className="mr-2 h-4 w-4" /> {t("nav.dashboard")}</DropdownMenuItem>
        {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><Shield className="mr-2 h-4 w-4" /> {t("nav.admin")}</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-deconnexion" className="text-rose-600"><LogOut className="mr-2 h-4 w-4" /> {t("nav.logout")}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const DesktopLink = ({ item }) => {
  const { t } = useI18n();
  return (
  <NavLink to={item.to} end={item.end} data-testid={item.testid}
    className={({ isActive }) => cn("relative rounded-lg px-3 py-2 text-sm font-medium transition-colors",
      isActive ? "text-primary" : "text-muted-foreground hover:text-primary hover:bg-muted")}>
    {({ isActive }) => (
      <>
        {t(item.labelKey, item.label)}
        {isActive && <motion.span layoutId="nav-underline" className="absolute inset-x-2 -bottom-[7px] h-0.5 rounded-full bg-secondary" transition={{ type: "spring", stiffness: 400, damping: 32 }} />}
      </>
    )}
  </NavLink>
  );
};

const Layout = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const [sheetOpen, setSheetOpen] = useState(false);
  useEffect(() => { setSheetOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-0">
      <header className="sticky top-0 z-40 border-b bg-card/85 backdrop-blur supports-[backdrop-filter]:bg-card/70">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/" data-testid="logo-lien"><Logo /></Link>
          <nav className="hidden lg:flex items-center gap-1">{NAV.map((item) => <DesktopLink key={item.to} item={item} />)}</nav>
          <div className="flex items-center gap-1.5">
            <LanguageToggle />
            <ThemeToggle />
            {user ? (
              <>
                {user.role === "admin" && <Button variant="ghost" size="sm" className="hidden md:inline-flex" onClick={() => navigate("/admin")} data-testid="header-admin-bouton"><Shield className="mr-1.5 h-4 w-4" /> {t("nav.adminShort")}</Button>}
                <NotificationPanel />
                <AvatarMenu />
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate("/connexion")} data-testid="header-connexion-bouton">{t("nav.login")}</Button>
                <Button size="sm" className="bg-secondary hover:bg-emerald-600 text-white" onClick={() => navigate("/inscription")} data-testid="header-inscription-bouton">{t("nav.register")}</Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="min-h-[60vh]">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t bg-card/95 pb-safe backdrop-blur md:hidden">
        <div className="grid grid-cols-5">
          {MOBILE_MAIN.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} data-testid={`${item.testid}-mobile`}
              className={({ isActive }) => cn("relative flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium", isActive ? "text-secondary" : "text-muted-foreground")}>
              {({ isActive }) => (
                <>
                  {isActive && <motion.span layoutId="bottomnav-pill" className="absolute -top-px h-0.5 w-8 rounded-full bg-secondary" />}
                  <item.icon className="h-5 w-5" />
                  {t(item.labelKey, item.label)}
                </>
              )}
            </NavLink>
          ))}
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <button data-testid="nav-plus-mobile" className="flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium text-muted-foreground"><Menu className="h-5 w-5" /> {t("nav.more")}</button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetHeader><SheetTitle>{t("nav.menu")}</SheetTitle></SheetHeader>
              <div className="mt-4 flex flex-col gap-1">
                {MOBILE_MORE.map((item) => (
                  <NavLink key={item.to} to={item.to} data-testid={`${item.testid}-sheet`}
                    className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium", isActive ? "bg-secondary/10 text-secondary" : "text-foreground hover:bg-muted")}>
                    <item.icon className="h-5 w-5" /> {t(item.labelKey, item.label)}
                  </NavLink>
                ))}
                {user && <NavLink to="/tableau-de-bord" data-testid="nav-dashboard-sheet" className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium", isActive ? "bg-secondary/10 text-secondary" : "text-foreground hover:bg-muted")}><LayoutDashboard className="h-5 w-5" /> {t("nav.dashboard")}</NavLink>}
                {user?.role === "admin" && <NavLink to="/admin" data-testid="nav-admin-sheet" className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium", isActive ? "bg-secondary/10 text-secondary" : "text-foreground hover:bg-muted")}><Shield className="h-5 w-5" /> {t("nav.admin")}</NavLink>}
                {!user && <NavLink to="/connexion" className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-foreground hover:bg-muted"><UserIcon className="h-5 w-5" /> {t("nav.login")}</NavLink>}
                {user && <button onClick={() => { navigate("/notifications"); }} className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-foreground hover:bg-muted"><Bell className="h-5 w-5" /> {t("nav.notifications")}</button>}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </div>
  );
};

export default Layout;
